import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Select,
  MenuItem,
  Button,
  Paper,
  TextField,
} from "@mui/material";
import { getWellConfig } from "../../utils/wellsConfig";
import { PRC_WELL } from "../../utils/PRC_WELL";
import { ProgressDialog } from "./progressPopup";
import axios from "../../utils/axiosInstance";
import {
  C5_REGISTERS,
  getSetpoints,
  requestSetpoints,
} from "../../utils/controlConfig";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";
import { toast } from "react-toastify";

const regListBodyForROCCommand = {
  ROC_ENABLE_DISABLE: 42431,
  ROC_OPEN_STATE: 42888,
  ROC_AF_STATE: 42889,
  ROC_SHUTIN_STATE: 42890,
  ROC_MANDATORY_SHUTIN_STATE: 42891,
};

const PROP_VALVE_CONTROL_OFFSETS = {
  1: {
    PROP_VALVE_CONTROL: 43979,
    PROP_VALVE_POS: 42403,
    PROP_MIN_OUTPUT: 43984,
    PROP_MAX_OUTPUT: 43985,
    PROP_MANUAL_SP: 44039,
  },
  2: {
    PROP_VALVE_CONTROL: 43986,
    PROP_VALVE_POS: 42404,
    PROP_MIN_OUTPUT: 43991,
    PROP_MAX_OUTPUT: 43992,
    PROP_MANUAL_SP: 44040,
  },
  3: {
    PROP_VALVE_CONTROL: 43993,
    PROP_VALVE_POS: 42405,
    PROP_MIN_OUTPUT: 43998,
    PROP_MAX_OUTPUT: 43999,
    PROP_MANUAL_SP: 44041,
  },
  4: {
    PROP_VALVE_CONTROL: 44000,
    PROP_VALVE_POS: 42406,
    PROP_MIN_OUTPUT: 44005,
    PROP_MAX_OUTPUT: 44006,
    PROP_MANUAL_SP: 44042,
  },
};

export default function OptimizationTab({
  optimizationSettings,
  proportionalValveControlsSettings,
  rocConfig,
  setLoading,
  selectedTab,
  selectedDevice,
  refreshData,
}) {
  const [optimizationValues, setOptimizationValues] =
    useState(optimizationSettings);
  const [proportionalValveControlsValues, setproportionalValveControlsValues] =
    useState(proportionalValveControlsSettings);
  const [showDependentFields, setShowDependentFields] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressSuccess, setProgressSuccess] = useState(false);
  const [progressError, setProgressError] = useState("");
  const [rocValues, setRocValues] = useState(rocConfig);

  useEffect(() => {
    setOptimizationValues(optimizationSettings);
  }, [optimizationSettings]);

  useEffect(() => {
    setproportionalValveControlsValues(proportionalValveControlsSettings);
  }, [proportionalValveControlsSettings]);

  useEffect(() => {
    const plungerFallItem = optimizationSettings.find(
      (item) => item.label === "Detect Plunger Fall",
    );
    const isEnabled =
      plungerFallItem?.value === "1" || plungerFallItem?.value === 1;
    setShowDependentFields(isEnabled);
  }, []);

  const visibleOptimizationValues = useMemo(() => {
    if (showDependentFields) {
      return optimizationValues;
    }
    return optimizationValues.filter(
      (item) =>
        item.label !== "Detect Ghost Arrivals" &&
        item.label !== "Shutin at Plunger Fall",
    );
  }, [optimizationValues, showDependentFields]);

  const handleChangeOptimization = (label, newValue) => {
    setOptimizationValues((prev) =>
      prev.map((item) =>
        item.label === label ? { ...item, value: newValue } : item,
      ),
    );
  };

  const handleChangeProportionalValve = (label, newValue) => {
    setproportionalValveControlsValues((prev) =>
      prev.map((item) =>
        item.label === label ? { ...item, value: newValue } : item,
      ),
    );
  };

  const handleChangeRoc = (label, value) => {
    setRocValues((prev) =>
      prev.map((item) => (item.label === label ? { ...item, value } : item)),
    );
  };

  const handleSubmit = async (label, value) => {
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 1: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 2: Give React a chance to render before doing heavy work
    setTimeout(() => {
      runDeviceUpdate(label, value);
    }, 100); // 100ms is enough to show the dialog
  };

  const handleSubmitPlgPropValve = async () => {
    // Step 1: Check if all fields are filled
    const isAnyFieldEmpty = proportionalValveControlsValues.some((item) => {
      return item.value === "";
    });

    if (isAnyFieldEmpty) {
      console.error("❌ Please fill all fields.");
      toast.error("❌ Please fill all fields before submitting.");
      return;
    }
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 1: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 2: Give React a chance to render before doing heavy work
    setTimeout(() => {
      updatePlgPropValve();
    }, 100); // 100ms is enough to show the dialog
  };

  const handleSubmitRoc = async () => {
    // Find the master controller item from your state array
  const rocEnableItem = rocValues.find((i) => i.label === "ROC Enable / Disable");
  const isRocEnabled = rocEnableItem ? String(rocEnableItem.value) === "1" : false;

  // Conditional Field Validation
  if (isRocEnabled) {
    // If ROC is enabled, EVERY dependent state field must be populated
    const isAnyFieldEmpty = rocValues.some((item) => item.value === "" || item.value == null);
    
    if (isAnyFieldEmpty) {
      console.error("❌ Please fill all fields.");
      toast.error("❌ Please fill all fields before submitting.");
      return; 
    }
  } else {
    // If ROC is disabled, just make sure the master toggle itself isn't left empty
    if (!rocEnableItem || rocEnableItem.value === "") {
      toast.error("❌ Please choose an option for ROC Enable / Disable.");
      return;
    }
  }
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 1: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 2: Give React a chance to render before doing heavy work
    setTimeout(() => {
      updateRoc();
    }, 100); // 100ms is enough to show the dialog
  };

  const runDeviceUpdate = async (label, value) => {
    let pollInterval = null;
    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollForChange = async ({
      unitId,
      registerList,
      lastTime,
      lastResponse,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
            registerList,
          );

          const { time: currentTime, response: currentResponse } =
            resp.data.data || {};

          const timeChanged =
            currentTime && Number(currentTime) !== Number(lastTime);

          let valueChanged = false;
          for (const key in currentResponse) {
            if (currentResponse[key] !== lastResponse[key]) {
              valueChanged = true;
              break;
            }
          }

          console.log("⏳ Polling...", {
            currentTime,
            timeChanged,
            valueChanged,
          });

          if (timeChanged && valueChanged) {
            stopPolling();
            onSuccess({
              currentTime,
              currentResponse,
            });
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };

    try {
      console.log(`Submitting: ${label} = ${value}`);
      let cmd = "A";
      let offset = "";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      if (label === "Overwatch") {
        offset = wellConfig.OVERWATCH - 40001;
      } else if (label === "Detect Plunger Fall") {
        offset = wellConfig.DET_PLG_FAIL - 40001;
      } else if (label === "Detect Ghost Arrivals") {
        offset = wellConfig.DET_GHOST_ARR - 40001;
      } else if (label === "Shutin at Plunger Fall") {
        offset = wellConfig.SHUTIN_PLG_FALL - 40001;
      } else if (label === "Casing Rise") {
        offset = wellConfig.CASING_RISE - 40001;
      } else if (label === "Critical Flow Rate") {
        offset = wellConfig.CRIT_FLOW_RATE - 40001;
      } else if (label === "Tubing : Casing") {
        offset = wellConfig.TUBING_CASING - 40001;
      }
      let registerList = [
        wellConfig.OVERWATCH,
        wellConfig.DET_PLG_FAIL,
        wellConfig.DET_GHOST_ARR,
        wellConfig.SHUTIN_PLG_FALL,
        wellConfig.CASING_RISE,
        wellConfig.CRIT_FLOW_RATE,
        wellConfig.TUBING_CASING,
      ];
      console.log("register list in body", registerList);
      // Step 1: get initial time
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        registerList,
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      let lastResponse = initialResp.data.data?.response || {};
      console.log("Initial time:", lastTime);
      console.log("regList:", lastResponse);
      const getValue = PRC_WELL.getValueFromWell(optimizationValues, label);
      cmd += "," + offset + "," + getValue.value;
      // Step 2: Send A command
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
        {
          unitId,
          cmd,
          UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd}`,
        },
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        },
      );
      console.log("Sending A command:", cmd);
      console.log("🟡 Polling for 30s...");
      // STEP 3: poll for 30s BEFORE sending P
      pollForChange({
        unitId,
        registerList,
        lastTime,
        lastResponse,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Update done after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === STEP 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            {
              unitId,
              cmd: pCmd,
              UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd}`,
            },
            { headers: { id: "anything", "Content-Type": "application/json" } },
          );

          console.log("🟢 P command sent, polling again...");

          // === STEP 5: poll AFTER P ===
          pollForChange({
            unitId,
            registerList,
            lastTime,
            lastResponse,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Update done after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again.",
              );
            },
          });
        },
      });
    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const updatePlgPropValve = async () => {
    let pollInterval = null;
    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };
    const pollForChange = async ({
      unitId,
      registerList,
      lastTime,
      lastResponse,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
            registerList,
          );

          const { time: currentTime, response: currentResponse } =
            resp.data.data || {};

          const timeChanged =
            currentTime && Number(currentTime) !== Number(lastTime);

          let valueChanged = false;
          for (const key in currentResponse) {
            if (currentResponse[key] !== lastResponse[key]) {
              valueChanged = true;
              break;
            }
          }

          console.log("⏳ Polling...", {
            currentTime,
            timeChanged,
            valueChanged,
          });

          if (timeChanged && valueChanged) {
            stopPolling();
            onSuccess({
              currentTime,
              currentResponse,
            });
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };
    try {
      let cmd = "A";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      let registerList = [
        wellConfig.PROP_VALVE_CONTROL,
        wellConfig.PROP_VALVE_POS,
        wellConfig.PROP_MIN_OUTPUT,
        wellConfig.PROP_MAX_OUTPUT,
        wellConfig.PROP_MANUAL_SP,
      ];
      console.log("register list in body", registerList);
      // Step 1: get initial time
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        registerList,
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      let lastResponse = initialResp.data.data?.response || {};
      console.log("Initial time:", lastTime);
      console.log("regList:", lastResponse);
      const propValveControl = PRC_WELL.getValueFromWell(
        proportionalValveControlsValues,
        "Prop Valve Control",
      );
      const valvePos = PRC_WELL.getValueFromWell(
        proportionalValveControlsValues,
        "Valve Position@4mA",
      );
      const minOut = PRC_WELL.getValueFromWell(
        proportionalValveControlsValues,
        "Minimum Output %",
      );
      const maxOut = PRC_WELL.getValueFromWell(
        proportionalValveControlsValues,
        "Maximum Output %",
      );
      const manualSP = PRC_WELL.getValueFromWell(
        proportionalValveControlsValues,
        "Manual SP",
      );
      cmd +=
        "," +
        (PROP_VALVE_CONTROL_OFFSETS[selectedTab].PROP_VALVE_CONTROL - 40001) +
        "," +
        propValveControl.value;
      cmd +=
        "," +
        (PROP_VALVE_CONTROL_OFFSETS[selectedTab].PROP_VALVE_POS - 40001) +
        "," +
        valvePos.value;
      cmd +=
        "," +
        (PROP_VALVE_CONTROL_OFFSETS[selectedTab].PROP_MIN_OUTPUT - 40001) +
        "," +
        minOut.value  * 100;
      cmd +=
        "," +
        (PROP_VALVE_CONTROL_OFFSETS[selectedTab].PROP_MAX_OUTPUT - 40001) +
        "," +
        maxOut.value * 100;
      cmd +=
        "," +
        (PROP_VALVE_CONTROL_OFFSETS[selectedTab].PROP_MANUAL_SP - 40001) +
        "," +
        manualSP.value * 100;
      // Step 2: Send A command
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
         {unitId, cmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd }`,},
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        },
      );
      console.log("Sending command:", cmd);
      console.log("🟡 Polling for 30s...");
      // STEP 3: poll for 30s BEFORE sending P
      pollForChange({
        unitId,
        registerList,
        lastTime,
        lastResponse,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Update done after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === STEP 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            {unitId, cmd: pCmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd }`,},
            { headers: { id: "anything", "Content-Type": "application/json" } },
          );

          console.log("🟢 P command sent, polling again...");

          // === STEP 5: poll AFTER P ===
          pollForChange({
            unitId,
            registerList,
            lastTime,
            lastResponse,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Update done after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again.",
              );
            },
          });
        },
      });
    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const updateRoc = async () => {
    let pollInterval = null;
    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollForChange = async ({
      unitId,
      registerList,
      lastTime,
      lastResponse,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
            registerList,
          );

          const { time: currentTime, response: currentResponse } =
            resp.data.data || {};

          const timeChanged =
            currentTime && Number(currentTime) !== Number(lastTime);

          let valueChanged = false;
          for (const key in currentResponse) {
            if (currentResponse[key] !== lastResponse[key]) {
              valueChanged = true;
              break;
            }
          }

          console.log("⏳ Polling...", {
            currentTime,
            timeChanged,
            valueChanged,
          });

          if (timeChanged && valueChanged) {
            stopPolling();
            onSuccess({
              currentTime,
              currentResponse,
            });
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };
    try {
      let cmd = "A";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      let registerList = [
        wellConfig.ROC_ENABLE_DISABLE,
        wellConfig.ROC_OPEN_STATE,
        wellConfig.ROC_AF_STATE,
        wellConfig.ROC_SHUTIN_STATE,
        wellConfig.ROC_MANDATORY_SHUTIN_STATE,
      ];
      console.log("register list in body", registerList);
      // Step 1: get initial time
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        registerList,
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      let lastResponse = initialResp.data.data?.response || {};
      console.log("Initial time:", lastTime);
      console.log("regList:", lastResponse);
      const rocValue = PRC_WELL.getValueFromWell(
        rocValues,
        "ROC Enable / Disable",
      );
      const openStateValue = PRC_WELL.getValueFromWell(rocValues, "Open State");
      const afStateValue = PRC_WELL.getValueFromWell(
        rocValues,
        "Afterflow State",
      );
      const shutinStateValue = PRC_WELL.getValueFromWell(
        rocValues,
        "Shutin State",
      );
      const mandShutinStateValue = PRC_WELL.getValueFromWell(
        rocValues,
        "Mandatory Shutin State",
      );
      cmd +=
        "," +
        (regListBodyForROCCommand.ROC_ENABLE_DISABLE - 40001) +
        "," +
        rocValue.value;
      if (String(rocValue.value) === "1") {
        cmd +=
          "," +
          (regListBodyForROCCommand.ROC_OPEN_STATE - 40001) +
          "," +
          openStateValue.value;
        cmd +=
          "," +
          (regListBodyForROCCommand.ROC_AF_STATE - 40001) +
          "," +
          afStateValue.value;
        cmd +=
          "," +
          (regListBodyForROCCommand.ROC_SHUTIN_STATE - 40001) +
          "," +
          shutinStateValue.value;
        cmd +=
          "," +
          (regListBodyForROCCommand.ROC_MANDATORY_SHUTIN_STATE - 40001) +
          "," +
          mandShutinStateValue.value;
      }
      console.log("Sending command:", cmd);
      // Step 2: Send A command
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
        {
          unitId,
          cmd,
          UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd}`,
        },
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        },
      );
      console.log("Sending command:", cmd);
      console.log("🟡 Polling for 30s...");
      // STEP 3: poll for 30s BEFORE sending P
      pollForChange({
        unitId,
        registerList,
        lastTime,
        lastResponse,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Update done after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === STEP 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            {
              unitId,
              cmd: pCmd,
              UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd}`,
            },
            { headers: { id: "anything", "Content-Type": "application/json" } },
          );

          console.log("🟢 P command sent, polling again...");

          // === STEP 5: poll AFTER P ===
          pollForChange({
            unitId,
            registerList,
            lastTime,
            lastResponse,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Update done after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again.",
              );
            },
          });
        },
      });
    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 12, lg: 6 }}>
          <Box sx={{ bgcolor: "#1e293b", px: 1, py: 1.5 }}>
            <Typography sx={{ color: "#ffffffff", fontSize: 14 }}>
              Enable/Disable Optimization Logic / Proportional Valve Control
            </Typography>
          </Box>
          {visibleOptimizationValues.map((item, index) => (
            <Grid
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #a0a0a0ff",
                p: 1,
              }}
            >
              <Typography
                sx={{
                  flex: 2,
                  color: "#ffffffff",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {item.label}
              </Typography>
              <Grid
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Grid sx={{ flex: 1 }}>
                  {item.label === "Overwatch" ? (
                    <Select
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={item.value ?? ""}
                      style={{ fontSize: 13 }}
                      onChange={(e) =>
                        handleChangeOptimization(item.label, e.target.value)
                      }
                      sx={{
                        backgroundColor: "#1f2937",
                        color: "#fff",
                        ".MuiSelect-icon": { color: "#fff" },
                        "& .MuiFilledInput-input": { textAlign: "center" },
                      }}
                    >
                      <MenuItem value="" disabled>
                        -- Select --
                      </MenuItem>
                      <MenuItem value="1" style={{ fontSize: 13 }}>
                        Flow Rate
                      </MenuItem>
                      <MenuItem value="2" style={{ fontSize: 13 }}>
                        Casing PSI
                      </MenuItem>
                      <MenuItem value="0" style={{ fontSize: 13 }}>
                        Disabled
                      </MenuItem>
                    </Select>
                  ) : (
                    <Select
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={item.value ?? ""}
                      style={{ fontSize: 13 }}
                      onChange={(e) =>
                        handleChangeOptimization(item.label, e.target.value)
                      }
                      sx={{
                        backgroundColor: "#1f2937",
                        color: "#fff",
                        ".MuiSelect-icon": { color: "#fff" },
                        "& .MuiFilledInput-input": { textAlign: "center" },
                      }}
                    >
                      <MenuItem value="" disabled>
                        -- Select --
                      </MenuItem>
                      y
                      <MenuItem value="1" style={{ fontSize: 13 }}>
                        Enabled
                      </MenuItem>
                      <MenuItem value="0" style={{ fontSize: 13 }}>
                        Disabled
                      </MenuItem>
                    </Select>
                  )}
                </Grid>
                <Grid>
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    sx={{ fontSize: 12 }}
                    onClick={() => handleSubmit(item.label, item.value)}
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Grid>
        <Grid
          size={{ xs: 12, md: 12, lg: 6 }}
          display={"flex"}
          flexDirection={"column"}
          gap={3}
        >
          {/* PROPORTIONAL VALVE CONTROLS */}
          <Grid>
            <Grid
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              sx={{ backgroundColor: "#1e293b" }}
              p={1}
            >
              <Typography fontSize={14}>Proportional Valve Controls</Typography>
              <Button
                color="primary"
                variant="contained"
                size="small"
                sx={{ fontSize: 12 }}
                onClick={() => handleSubmitPlgPropValve()}
              >
                Submit
              </Button>
            </Grid>
            <Grid px={1}>
              {proportionalValveControlsValues.map((item, index) => (
                <Grid
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid #a0a0a0ff",
                    py: 1,
                  }}
                >
                  <Typography
                    sx={{
                      flex: 2,
                      color: "#ffffffff",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Grid
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Grid sx={{ flex: 1 }}>
                      {(() => {
                        switch (item.label) {
                          // 1. Dropdown for Prop Valve Control
                          case "Prop Valve Control":
                            return (
                              <Select
                                variant="outlined"
                                size="small"
                                value={item.value ?? ""}
                                style={{ fontSize: 13 }}
                                onChange={(e) =>
                                  handleChangeProportionalValve(
                                    item.label,
                                    e.target.value,
                                  )
                                }
                                sx={{
                                  backgroundColor: "#1f2937",
                                  color: "#fff",
                                  ".MuiSelect-icon": { color: "#fff" },
                                  "& .MuiFilledInput-input": {
                                    textAlign: "center",
                                  },
                                }}
                              >
                                <MenuItem value="" disabled>
                                  -- Select --
                                </MenuItem>
                                {/* Swap 0, 1, 2 for whatever your Modbus register values map to */}
                                <MenuItem value="0" style={{ fontSize: 13 }}>
                                  Manual
                                </MenuItem>
                                <MenuItem value="1" style={{ fontSize: 13 }}>
                                  Casing PSI
                                </MenuItem>
                                <MenuItem value="2" style={{ fontSize: 13 }}>
                                  Flow Rate
                                </MenuItem>
                              </Select>
                            );

                          // 2. Dropdown for Valve Position
                          case "Valve Position@4mA":
                            return (
                              <Select
                                variant="outlined"
                                size="small"
                                value={item.value ?? ""}
                                style={{ fontSize: 13 }}
                                onChange={(e) =>
                                  handleChangeProportionalValve(
                                    item.label,
                                    e.target.value,
                                  )
                                }
                                sx={{
                                  backgroundColor: "#1f2937",
                                  color: "#fff",
                                  ".MuiSelect-icon": { color: "#fff" },
                                  "& .MuiFilledInput-input": {
                                    textAlign: "center",
                                  },
                                }}
                              >
                                <MenuItem value="" disabled>
                                  -- Select --
                                </MenuItem>
                                <MenuItem value="1" style={{ fontSize: 13 }}>
                                  Closed
                                </MenuItem>
                                <MenuItem value="0" style={{ fontSize: 13 }}>
                                  Open
                                </MenuItem>
                              </Select>
                            );

                          // 3. Text inputs for Min Output, Max Output, and Manual SP
                          default:
                            return (
                              <TextField
                                variant="outlined"
                                value={item.value ?? ""}
                                fullWidth
                                size="small"
                                onChange={(e) =>
                                  handleChangeProportionalValve(
                                    item.label,
                                    e.target.value,
                                  )
                                }
                                InputLabelProps={{
                                  style: { color: "#fff", fontSize: 14 },
                                }}
                                InputProps={{
                                  sx: {
                                    color: "#fff",
                                    backgroundColor: "#1f2937",
                                    padding: 0.2,
                                    fontSize: 12,
                                  },
                                }}
                              />
                            );
                        }
                      })()}
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
          {/* ROC */}
          <Grid>
            {selectedTab === 1 && (
              <Grid>
                <Grid
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  sx={{ backgroundColor: "#1e293b" }}
                  p={1}
                >
                  <Typography fontSize={14}>
                    PRC DEMO - ROC Configuration
                  </Typography>
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    sx={{ fontSize: 12 }}
                    onClick={() => handleSubmitRoc()}
                  >
                    Submit
                  </Button>
                </Grid>
                <Grid>
                  {rocValues.map((item, index) => {
                    const rocEnableKey = rocValues.find(
                      (i) => i.label === "ROC Enable / Disable",
                    );
                    const rocEnabled = rocEnableKey
                      ? String(rocEnableKey?.value) === "1"
                      : false;

                    return (
                      <Grid
                        key={index}
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        p={1}
                        borderBottom={"1px solid #a0a0a0ff"}
                      >
                        <Grid>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: 13,
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Grid>
                        <Grid display={"flex"} alignItems={"center"} gap={4}>
                          {item.label === "ROC Enable / Disable" ? (
                            <Select
                              variant="outlined"
                              size="small"
                              value={item.value ?? ""}
                              style={{ fontSize: 13 }}
                              onChange={(e) =>
                                handleChangeRoc(item.label, e.target.value)
                              }
                              sx={{
                                backgroundColor: "#1f2937",
                                color: "#fff",
                                ".MuiSelect-icon": { color: "#fff" },
                                "& .MuiFilledInput-input": {
                                  textAlign: "center",
                                },
                              }}
                            >
                              <MenuItem value="" disabled>
                                -- Select --
                              </MenuItem>
                              <MenuItem value="1" style={{ fontSize: 13 }}>
                                Enabled
                              </MenuItem>
                              <MenuItem value="0" style={{ fontSize: 13 }}>
                                Disabled
                              </MenuItem>
                            </Select>
                          ) : (
                            <Select
                              variant="outlined"
                              size="small"
                              value={item.value ?? ""}
                              style={{ fontSize: 13 }}
                              disabled={!rocEnabled}
                              onChange={(e) =>
                                handleChangeRoc(item.label, e.target.value)
                              }
                              sx={{
                                backgroundColor: "#1f2937",
                                color: "#fff",
                                ".MuiSelect-icon": { color: "#fff" },
                                "& .MuiFilledInput-input": {
                                  textAlign: "center",
                                },
                              }}
                            >
                              <MenuItem value="" disabled>
                                -- Select --
                              </MenuItem>
                              <MenuItem value="1" style={{ fontSize: 13 }}>
                                Loaded
                              </MenuItem>
                              <MenuItem value="0" style={{ fontSize: 13 }}>
                                Unloaded
                              </MenuItem>
                            </Select>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <ProgressDialog
        open={progressOpen}
        isSuccess={progressSuccess}
        errorMessage={progressError}
        onClose={() => {
          setProgressOpen(false);
          setProgressError("");
        }}
        onRefresh={async () => {
          setProgressOpen(false);
          setProgressError("");
          await refreshData();
        }}
        loadingMessage="Check Device Response. Operation in-progress ..."
        successMessage="Successfully updated!"
      />
    </Box>
  );
}
